import { useEffect, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';

export const usePostRender = (itemId: string | undefined, isOpen: boolean) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoadingRender, setIsLoadingRender] = useState(false);

  // Fetch render image URL when modal opens
  useEffect(() => {
    const fetchRenderImage = async () => {
      if (!itemId || !isOpen) {
        setImageUrl(null);
        return;
      }

      setIsLoadingRender(true);
      try {
        const response = await api.get(`/public/posts/${itemId}/render`);
        if (response.data?.imageUrl) {
          setImageUrl(response.data.imageUrl);
        }
      } catch (error: unknown) {
        console.error('Failed to fetch render image URL:', getErrorMessage(error));
      } finally {
        setIsLoadingRender(false);
      }
    };

    fetchRenderImage();
  }, [itemId, isOpen]);

  const triggerRender = async (itemId: string, payload: any): Promise<boolean> => {
    try {
      await api.post(`/mxm/posts/${itemId}/render`, payload);
      return true;
    } catch (error: unknown) {
      console.error('Failed to trigger render:', getErrorMessage(error));
      return false;
    }
  };

  const fetchUpdatedRender = async (itemId: string, maxAttempts = 3): Promise<boolean> => {
    const attempt = async (attemptNum: number): Promise<boolean> => {
      const response = await api.get(`/public/posts/${itemId}/render`);
      if (response.data?.imageUrl) {
        setImageUrl(response.data.imageUrl);
        return true;
      }
      if (attemptNum < maxAttempts) {
        await new Promise((r) => setTimeout(r, 1500));
        return attempt(attemptNum + 1);
      }
      return false;
    };

    return attempt(1);
  };

  return {
    imageUrl,
    setImageUrl,
    isLoadingRender,
    setIsLoadingRender,
    triggerRender,
    fetchUpdatedRender,
  };
};

